import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MapPin, Lock, Loader2, Navigation, Coins, Plus, X, Clock } from "lucide-react";

const HuntCard = ({ h, onChange }) => {
  const [busy, setBusy] = useState(false);
  const unlock = async () => {
    setBusy(true);
    try { await api.post(`/cardwars/hunts/${h.id}/unlock`); toast.success("Clue unlocked"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const claim = () => {
    if (!navigator.geolocation) return toast.error("Location isn't supported on this device");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.post(`/cardwars/hunts/${h.id}/claim`, { lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success(`🎉 YOU FOUND A BULLPEN DROP! +${data.reward_blc} BLC`, { duration: 8000 });
        onChange();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      setBusy(false);
    }, () => { toast.error("Couldn't get your location — allow location access and try again"); setBusy(false); },
    { enableHighAccuracy: true, timeout: 12000 });
  };
  return (
    <div className="border border-[#A3E635]/40 bg-pb-surface p-4" data-testid={`hunt-card-${h.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="w-4 h-4 text-[#A3E635] shrink-0" />
        <span className="font-display font-black uppercase text-sm flex-1 min-w-0 truncate">{h.title}</span>
        <span className="shrink-0 text-[9px] font-black uppercase tracking-widest text-yellow-400 border border-yellow-400/50 px-2 py-0.5 flex items-center gap-1">
          <Coins className="w-2.5 h-2.5" /> {h.reward_blc} BLC
        </span>
      </div>
      <div className="space-y-1 mb-3">
        {h.clues.map((c, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-zinc-300 bg-pb-elevated/50 px-2.5 py-1.5" data-testid={`hunt-${h.id}-clue-${i + 1}`}>
            <span className="text-[9px] font-black text-[#A3E635] mt-0.5 shrink-0">#{i + 1}</span>
            <span className="italic">{c}</span>
          </div>
        ))}
        {Array.from({ length: h.total_clues - h.unlocked }).map((_, i) => (
          <div key={`l${i}`} className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-600 border border-dashed border-pb-border px-2.5 py-1.5">
            <Lock className="w-3 h-3" /> Clue #{h.unlocked + i + 1} locked
          </div>
        ))}
        {h.next_free_clue_min != null && h.unlocked < h.total_clues && (
          <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-[#A3E635]" data-testid={`hunt-${h.id}-next-free`}>
            <Clock className="w-3 h-3" /> next free clue in ~{h.next_free_clue_min >= 60 ? `${Math.floor(h.next_free_clue_min / 60)}h ${h.next_free_clue_min % 60}m` : `${h.next_free_clue_min}m`}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        {h.unlocked < h.total_clues && (
          <button data-testid={`hunt-${h.id}-unlock-btn`} onClick={unlock} disabled={busy}
            className="flex-1 text-[10px] font-black uppercase tracking-widest py-2.5 border border-[#A3E635]/50 text-[#A3E635] hover:bg-[#A3E635] hover:text-black transition-colors disabled:opacity-40">
            Unlock next clue · {h.clue_cost_blc} BLC
          </button>
        )}
        <button data-testid={`hunt-${h.id}-claim-btn`} onClick={claim} disabled={busy}
          className="flex-1 flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-widest py-2.5 bg-[#A3E635] text-black hover:bg-yellow-400 transition-colors disabled:opacity-40">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Navigation className="w-3 h-3" />} I'm Here — Claim Drop
        </button>
      </div>
    </div>
  );
};

const AdminHuntForm = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", lat: "", lng: "", radius_m: 75, reward_blc: 5, clue_cost_blc: 1, free_clue_hours: 3, clues: "" });
  const [busy, setBusy] = useState(false);
  const useMyLocation = () => {
    navigator.geolocation?.getCurrentPosition(
      (p) => setF((x) => ({ ...x, lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) })),
      () => toast.error("Couldn't get your location"));
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/cardwars/admin/hunts", { ...f, lat: Number(f.lat), lng: Number(f.lng),
        radius_m: Number(f.radius_m), reward_blc: Number(f.reward_blc), clue_cost_blc: Number(f.clue_cost_blc),
        free_clue_hours: Number(f.free_clue_hours), clues: f.clues.split("\n") });
      toast.success("Bullpen Drop is live!");
      setOpen(false); setF({ title: "", lat: "", lng: "", radius_m: 75, reward_blc: 5, clue_cost_blc: 1, free_clue_hours: 3, clues: "" });
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!open) return (
    <button data-testid="hunt-admin-open-btn" onClick={() => setOpen(true)}
      className="w-full flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest py-2.5 border border-dashed border-yellow-400/50 text-yellow-400 hover:bg-yellow-400/10 transition-colors">
      <Plus className="w-3.5 h-3.5" /> Admin: Drop a Hunt
    </button>
  );
  return (
    <div className="border border-yellow-400/50 bg-pb-surface p-4" data-testid="hunt-admin-form">
      <div className="flex items-center justify-between mb-3">
        <span className="font-display font-black uppercase text-sm text-yellow-400">Drop a Hunt</span>
        <button onClick={() => setOpen(false)}><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
      </div>
      <div className="space-y-2">
        <input data-testid="hunt-form-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Drop title (e.g. Sunset Park Stash)"
          className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-xs outline-none focus:border-yellow-400/60" />
        <div className="flex gap-2">
          <input data-testid="hunt-form-lat" value={f.lat} onChange={(e) => setF({ ...f, lat: e.target.value })} placeholder="Latitude"
            className="flex-1 min-w-0 bg-pb-elevated border border-pb-border px-3 py-2 text-xs font-mono outline-none focus:border-yellow-400/60" />
          <input data-testid="hunt-form-lng" value={f.lng} onChange={(e) => setF({ ...f, lng: e.target.value })} placeholder="Longitude"
            className="flex-1 min-w-0 bg-pb-elevated border border-pb-border px-3 py-2 text-xs font-mono outline-none focus:border-yellow-400/60" />
          <button data-testid="hunt-form-use-location" onClick={useMyLocation} title="Use my location"
            className="shrink-0 px-3 border border-pb-border text-zinc-400 hover:text-yellow-400 hover:border-yellow-400/60"><Navigation className="w-3.5 h-3.5" /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[["radius_m", "Radius (m)"], ["reward_blc", "Reward BLC"], ["clue_cost_blc", "Clue cost BLC"], ["free_clue_hours", "Free clue every (hrs)"]].map(([k, ph]) => (
            <div key={k}>
              <div className="text-[8px] uppercase tracking-widest text-zinc-500 mb-1">{ph}</div>
              <input data-testid={`hunt-form-${k}`} type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-xs font-mono outline-none focus:border-yellow-400/60" />
            </div>
          ))}
        </div>
        <textarea data-testid="hunt-form-clues" value={f.clues} onChange={(e) => setF({ ...f, clues: e.target.value })} rows={5}
          placeholder={"One clue per line, HARDEST first (3-10 clues)\nWhere champions are made\nFollow the sound of dinks\nCourt 1, under the scoreboard"}
          className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-xs outline-none focus:border-yellow-400/60" />
        <button data-testid="hunt-form-submit" onClick={submit} disabled={busy || !f.lat || !f.lng}
          className="w-full bg-yellow-400 text-black font-black uppercase tracking-widest py-2.5 text-[10px] hover:bg-[#BEF264] disabled:opacity-40">
          {busy ? "Dropping…" : "Drop It"}
        </button>
      </div>
    </div>
  );
};

export default function HuntsSection() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/cardwars/hunts").then(({ data }) => setData(data)).catch(() => setData({ live: [], recent: [] }));
  }, []);
  useEffect(load, [load]);
  return (
    <div className="bg-pb-surface border border-pb-border p-5 animate-fade-up" data-testid="hunts-section">
      <div className="font-display font-black uppercase flex items-center gap-2 mb-1">
        <MapPin className="w-4 h-4 text-[#A3E635]" /> Bullpen Hunts
        <span className="text-[8px] font-black uppercase tracking-[0.25em] text-[#A3E635] border border-[#A3E635]/50 px-2 py-0.5 ml-1">Live</span>
      </div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-4">GPS drops — crack the clues, get there first, claim the reward</p>
      {!data ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-[#A3E635] animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {data.live.length === 0 && <div className="text-xs text-zinc-600 border border-dashed border-pb-border p-4 text-center" data-testid="hunts-empty">No live drops right now — keep an eye out.</div>}
          {data.live.map((h) => <HuntCard key={h.id} h={h} onChange={load} />)}
          {data.recent.length > 0 && (
            <div className="pt-1">
              <div className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-600 mb-1.5">Recently Found</div>
              {data.recent.map((h) => (
                <div key={h.id} className="flex items-center gap-2 text-xs text-zinc-500 py-1" data-testid={`hunt-recent-${h.id}`}>
                  <MapPin className="w-3 h-3 shrink-0" /> <span className="truncate flex-1">{h.title}</span>
                  <span className="text-zinc-400 shrink-0">found by <b className="text-[#A3E635]">{h.claimed_by_name}</b></span>
                </div>
              ))}
            </div>
          )}
          {user?.role === "admin" && <AdminHuntForm onCreated={load} />}
        </div>
      )}
    </div>
  );
}
